
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Star } from 'lucide-react';

interface TestimonialsProps {
  className?: string;
}

const testimonials = [
  {
    id: 1,
    name: 'Julie Blanchard',
    position: 'Cliente résidentielle',
    quote: 'La meilleure équipe à Valcartier, Shannon et Sainte Catherine de La Jacques-Cartier. 100% de confiance en eux. Travail professionnel et méticuleux. Faites-vous plaisir cet été, ça en vaut la peine !',
    quoteShort: 'La meilleure équipe dans la région. Travail professionnel et méticuleux !',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
    rating: 5,
    time: 'Il y a 1 mois'
  },
  {
    id: 2,
    name: 'Jérémy Fournier',
    position: 'Client résidentiel',
    quote: 'WOW! J\'ai fait appel à l\'équipe pour déneiger mon toit. J\'ai maintenant un toit déneigé à 100% et ma femme n\'a plus de stress pour sortir dehors. Les jeunes chez Entretiens McBain sont dynamiques et super sympathiques. Je recommande !!',
    quoteShort: 'Service de déneigement exceptionnel! Équipe dynamique et sympathique.',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop',
    rating: 5,
    time: 'Il y a 2 mois'
  },
  {
    id: 3,
    name: 'Catherine Rouleau',
    position: 'Cliente résidentielle',
    quote: 'Services 5 étoiles! Équipe courtoise et très professionnelle. Très satisfaite des services fournis, que ce soit pour le déneigement ou la tonte de pelouse. Je recommande fortement Entretiens McBain!',
    quoteShort: 'Services 5 étoiles! Équipe courtoise et très professionnelle.',
    image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=400&fit=crop',
    rating: 5,
    time: 'Il y a 2 mois'
  },
  {
    id: 4,
    name: 'Lucile Mrgt',
    position: 'Cliente résidentielle',
    quote: 'Je recommande Wilson et son équipe pour leur efficacité dans le service de déneigement, vous pouvez leur faire confiance sans hésitation!',
    quoteShort: 'Efficacité garantie pour le déneigement. Confiance totale!',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
    rating: 5,
    time: 'Il y a 2 mois'
  },
  {
    id: 5,
    name: 'Logistique Palettes',
    position: 'Client commercial',
    quote: 'Nous faisons affaire avec Entretien Mcbain depuis 2 ans. Rien de négatif à dire. Très ponctuel, équipe dynamique, toujours un résultat exceptionnel. Je vous recommande à 100%',
    quoteShort: 'Ponctualité et résultats exceptionnels. Recommande à 100%.',
    image: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=400&h=400&fit=crop',
    rating: 5,
    time: 'Il y a 8 mois'
  },
  {
    id: 6,
    name: 'Zakary Sauve',
    position: 'Client résidentiel',
    quote: 'J\'ai fait appel aux Services d\'Entretien McBain pour couper mon gazon. Excellent travail et je tiens à souligner l\'excellent travail de ces employés.',
    quoteShort: 'Excellent travail pour la tonte de pelouse. Employés remarquables.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    rating: 5,
    time: 'Il y a 8 mois'
  }
];

const Testimonials: React.FC<TestimonialsProps> = ({ className }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }
    
    return () => {
      if (contentRef.current) {
        observer.unobserve(contentRef.current);
      }
    };
  }, []);

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(
      () => setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length),
      6000 // Auto advance every 6 seconds
    );

    return () => {
      resetTimeout();
    };
  }, [activeIndex]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star 
        key={i}
        className={cn(
          "w-4 h-4 md:w-5 md:h-5", 
          i < rating ? "text-amber-400 fill-amber-400" : "text-gray-300"
        )}
      />
    ));
  };

  return (
    <section ref={sectionRef} className={cn(
      "section py-14 md:py-20 bg-gradient-to-b from-brand-stone to-white", 
      className
    )}>
      <div className="container-tight">
        <div ref={contentRef} className="text-center mb-8 md:mb-12 reveal-on-scroll">
          <h2 className="section-title mb-3">Ce que nos clients disent</h2>
          {!isMobile && (
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
              Découvrez les témoignages authentiques de nos clients satisfaits à travers la région.
            </p>
          )}
        </div>
        
        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden">
            <div 
              className="transition-all duration-700 ease-in-out flex" 
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="min-w-full px-4"
                >
                  <div className={cn(
                    "bg-white rounded-lg shadow-lg",
                    isMobile ? "p-4" : "p-8 md:p-10"
                  )}>
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
                      {!isMobile && (
                        <div className="flex-shrink-0">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name} 
                            className="w-24 h-24 rounded-full object-cover border-4 border-brand-stone"
                          />
                        </div>
                      )}
                      <div className="flex flex-col text-center md:text-left">
                        <div className="flex mb-2 justify-center md:justify-start">
                          {renderStars(testimonial.rating)}
                          <span className="text-xs text-gray-500 ml-2 self-end">{testimonial.time}</span>
                        </div>
                        
                        <p className={cn(
                          "text-gray-700 italic mb-4 md:mb-6",
                          isMobile ? "text-base" : "text-lg"
                        )}>
                          "{isMobile ? testimonial.quoteShort : testimonial.quote}"
                        </p>
                        <div>
                          <h4 className="font-display text-lg md:text-xl font-medium">{testimonial.name}</h4>
                          <p className="text-brand-sage text-sm md:text-base">{testimonial.position}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Testimonial navigation dots */}
          <div className="flex justify-center mt-4 md:mt-8 space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "rounded-full transition-all duration-300",
                  activeIndex === index
                    ? "bg-brand-green w-8 md:w-12 h-2 md:h-3"
                    : "bg-gray-300 hover:bg-gray-400 w-2 md:w-3 h-2 md:h-3"
                )}
                aria-label={`Aller au témoignage ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
